import type { ClientInput, Exercise } from '../types';
import type { Tracer } from '../tracer';

/**
 * Exercise selection rule — filter the master exercise list by:
 *   1. Equipment available to the client.
 *   2. Injury contraindications (matched against `Exercise.contraindications`).
 *   3. Difficulty appropriate to experience.
 *
 * Returns the pool that downstream rules draw from when building each day.
 */
export function exerciseSelectionRule(
  client: ClientInput,
  exercises: Exercise[],
  trace: Tracer,
): Exercise[] {
  const equipmentSet = new Set(client.equipment_available);
  const contraindicationTags = injuriesToTags(client.injuries);

  const pool: Exercise[] = [];
  const excludedByInjury: string[] = [];
  const excludedByEquipment: string[] = [];
  const excludedByDifficulty: string[] = [];

  for (const ex of exercises) {
    // Equipment check — every required item must be present, or be 'bodyweight'.
    const hasEquipment = ex.equipment_required.every(
      (e) => equipmentSet.has(e) || e === 'bodyweight',
    );
    if (!hasEquipment) {
      excludedByEquipment.push(ex.id);
      continue;
    }

    // Injury contraindication check.
    const blocked = ex.contraindications.some((c) => contraindicationTags.has(c.toLowerCase()));
    if (blocked) {
      excludedByInjury.push(ex.id);
      trace.emit(
        'exercise_selection',
        `excluded ${ex.name}`,
        `Contraindicated for client injuries.`,
        {
          exercise_id: ex.id,
          movement: ex.movement_pattern,
          contraindications: ex.contraindications,
          injuries: Array.from(contraindicationTags),
        },
      );
      continue;
    }

    // Difficulty check — beginners get beginner+intermediate only.
    if (client.experience_level === 'beginner' && ex.difficulty === 'advanced') {
      excludedByDifficulty.push(ex.id);
      continue;
    }

    pool.push(ex);
  }

  trace.emit(
    'exercise_selection',
    `${pool.length} candidates`,
    `Excluded: ${excludedByInjury.length} for injury, ${excludedByEquipment.length} for equipment, ${excludedByDifficulty.length} for difficulty.`,
    {
      candidates: pool.length,
      excluded_injury: excludedByInjury.length,
      excluded_equipment: excludedByEquipment.length,
      excluded_difficulty: excludedByDifficulty.length,
    },
  );

  return pool;
}

/**
 * Map free-text injury body parts to contraindication tags. Conservative —
 * unknown tags simply don't filter anything (the trainer still reviews).
 */
function injuriesToTags(injuries: ClientInput['injuries']): Set<string> {
  const tags = new Set<string>();
  for (const i of injuries) {
    const part = i.body_part.toLowerCase();
    if (
      /\b(lower\s?back|lumbar|l[1-5][-/ ]?l[1-5]|sciatica|disc|hernia)\b/.test(part) ||
      part.includes('lędźwiowy') ||
      part.includes('plec')
    ) {
      tags.add('lower_back_injury');
      tags.add('spinal_flexion_loaded');
    }
    if (/\bknee|kolan/.test(part)) {
      tags.add('knee_issue');
      tags.add('deep_knee_flexion');
    }
    if (/\bshoulder|bark|stawu\s+barkowego/.test(part)) {
      tags.add('shoulder_impingement');
      tags.add('overhead_pressing');
    }
    if (/\belbow|łokie/.test(part)) {
      tags.add('elbow_issue');
    }
    if (/\bwrist|nadgarstk/.test(part)) {
      tags.add('wrist_issue');
    }
    if (/\bhip|biodro/.test(part)) {
      tags.add('hip_issue');
    }
    if (/\bankle|kostka|skrę/.test(part)) {
      tags.add('ankle_issue');
    }
    if (/\bneck|szyj|cervical|c[1-7][-/ ]?c[1-7]/.test(part)) {
      tags.add('cervical_loading');
    }
  }
  return tags;
}

export const _internal = { injuriesToTags };
